import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';

import {
  favoriteProject,
  readProject,
  renameProject,
  updateFlowState,
  updateFlowStateTopicIds,
} from '@api/narrative-projects';
import { mapFlowStateComponentsToDrafts } from '@api/narrative-projects/utilities/helpers';
import {
  createNarrative,
  readDraftTopics,
  reorderFlowTopics,
  softDeleteFlowTopic,
  updateTopicVisibility,
} from '@api/narratives';
import { EditableTitle, FavoriteToggle, toast } from '@utilities';

import Footer from '../../../../../../components/Footer';
import ConfirmRemoveTopicModal from '../ConfirmRemoveTopicModal';
import PreBuildModal from '../../../../components/PreBuildModal';
import NarrativeFlowView from '../../../../components/NarrativeFlowView';
import NarrativeTitle from '../../../../components/NarrativeFlowView/components/NarrativeTitle';
import NarrativeTopicCard from '../../../../components/NarrativeFlowView/components/NarrativeTopicCard';
import ModalBuildNarrative from '../../../Narratives/components/ModalBuildNarrative';
import {
  getPromptPillTooltip,
  getPromptPillValue,
  transformPromptSelections,
} from '../../../Narratives/utilities/helpers';

import CanvasFloatingToolbar from './components/CanvasFloatingToolbar';
import RunFlowTopicsObserver from './components/RunFlowTopicsObserver';
import {
  buildEnrichedDraftTopics,
  buildManualSelectedDrafts,
  buildPromptAnswersPayload,
  extractComponentIds,
  getRunComponentIds,
  mergeDraftTopics,
  mergeUniqueValues,
  pruneDraftDatasetMap,
} from './utilities/helpers';
import {
  COMPLETED_OR_ERROR_FLOW_STATUSES,
  DEFAULT_PROMPT_ROUTE_LABEL,
  TOPIC_CARD_MOTION_PROPS,
} from './utilities/constants';
import { DEFAULT_DRAFT_NARRATIVE_NAME } from '../../utilities/constants';

import {
  areRequiredPromptsFilled,
  buildDraftFlowData,
  buildDraftTopicData,
  buildEditablePromptsFromFlowState,
  buildTopicShell,
  EMPTY_DOWNLOAD_MAP,
  extractEditablePromptAnswers,
  getRunTopicComponentId,
  hasPromptRequirements,
} from '../../utilities/helpers';

import styles from './_index.module.scss';

const isDraftContextUnchanged = (latestContext, requestContext) => {
  if (latestContext.chatSessionId !== requestContext.chatSessionId) return false;
  if (latestContext.flowId !== requestContext.flowId) return false;
  if (latestContext.draftIdentitySignature !== requestContext.draftIdentitySignature) return false;
  if (requestContext.requireMatchingDraftSignature) {
    if (latestContext.draftSignature !== requestContext.draftSignature) return false;
  }
  if (!requestContext.wasViewingDraft || !latestContext.isViewingDraft) return false;
  return true;
};

const isRunContextUnchanged = (latestContext, requestContext) => {
  if (latestContext.chatSessionId !== requestContext.chatSessionId) return false;
  if (latestContext.flowId === requestContext.flowId) return true;
  if (requestContext.allowNextFlowIdMatch && latestContext.flowId === requestContext.nextFlowId) {
    return true;
  }
  return false;
};

const normalizeTopicIds = (topicIds = []) => {
  if (!Array.isArray(topicIds)) return [];
  return topicIds.filter(
    (topicId) => topicId != null && (typeof topicId !== 'string' || topicId.trim().length > 0)
  );
};

const NarrativesCanvas = ({
  chatSessionId,
  flowId,
  flowState,
  onFlowSelected,
  onFlowStateChange,
  onRunFlowTopicIdsChange,
  onSelectedTopicsChange,
  runFlowRefreshKey = 0,
  selectedTopicCards = [],
}) => {
  const [activePrompt, setActivePrompt] = useState(null);
  const [buildError, setBuildError] = useState(null);
  const [draftDatasetMap, setDraftDatasetMap] = useState({});
  const [isBuilding, setIsBuilding] = useState(false);
  const [pendingDraftAction, setPendingDraftAction] = useState(null);
  const [pendingRunTopicAction, setPendingRunTopicAction] = useState(null);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [project, setProject] = useState(null);
  const [promptModalName, setPromptModalName] = useState(DEFAULT_DRAFT_NARRATIVE_NAME);
  const [selectedRunPrompt, setSelectedRunPrompt] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [transformedAnswersMap, setTransformedAnswersMap] = useState({});
  const [topicPendingDeletion, setTopicPendingDeletion] = useState(null);

  const previousProjectNameRef = useRef(project?.name || 'Untitled Project');
  const promptSelectionsSnapshotRef = useRef(null);
  const shouldPersistPromptChangesRef = useRef(false);
  const lastPersistedDraftSignatureRef = useRef(null);
  const draftPersistenceRequestIdRef = useRef(0);
  const hasUserEditedDraftNameRef = useRef(false);
  const latestDraftPersistContextRef = useRef({
    chatSessionId: null,
    draftIdentitySignature: null,
    draftSignature: null,
    flowId: null,
    isViewingDraft: false,
  });
  const runStateSyncRequestIdRef = useRef(0);
  const latestRunSyncContextRef = useRef({
    chatSessionId: null,
    flowId: null,
  });
  const promptRequirementsHydrationSignatureRef = useRef(null);
  const promptRequirementsHydrationRequestIdRef = useRef(0);
  const latestPromptRequirementsHydrationContextRef = useRef({
    chatSessionId: null,
    isViewingDraft: false,
    topicIdsSignature: '[]',
  });
  const latestFlowStateRef = useRef(flowState);

  const prompts = useMemo(() => buildEditablePromptsFromFlowState(flowState), [flowState]);
  const visiblePrompts = useMemo(() => prompts.filter((prompt) => !prompt?.isAdvanced), [prompts]);
  const canSelectPrompts = hasPromptRequirements(flowState) && visiblePrompts.length > 0;
  const isViewingRun = Boolean(flowId);
  const isViewingDraft = Boolean(flowState) && !isViewingRun;

  const narrativeFlow = useSelector((state) => {
    if (!flowId) return null;

    const flows = state?.websocket?.flows;
    if (!Array.isArray(flows)) return null;

    return flows.find((flow) => Number(flow?.id) === Number(flowId)) || null;
  });
  const userPermissions = useSelector((state) => state?.user?.data?.permissions) || {};
  const canManageRunTopics = Boolean(userPermissions?.canAccessChat);

  const flowStateDraftTopics = useMemo(
    () => (flowState ? mapFlowStateComponentsToDrafts(flowState) : []),
    [flowState]
  );

  const manualSelectedDrafts = useMemo(
    () => buildManualSelectedDrafts(selectedTopicCards),
    [selectedTopicCards]
  );
  const manualTopicOrder = useMemo(
    () => manualSelectedDrafts.map((draft) => draft.componentId),
    [manualSelectedDrafts]
  );
  const manualCardRefs = useRef(new Map());
  const manualCardRefCallbacks = useRef(new Map());
  const draftScrollContainerRef = useRef(null);
  const manualOrderChangeSourceRef = useRef(false);
  const previousManualCountRef = useRef(manualTopicOrder.length);
  const previousManualOrderRef = useRef(manualTopicOrder);

  const draftTopics = useMemo(
    () => mergeDraftTopics(flowStateDraftTopics, manualSelectedDrafts),
    [flowStateDraftTopics, manualSelectedDrafts]
  );
  const hasDraftTopicCards = draftTopics.length > 0;

  const componentIdsRef = useRef([]);
  const componentIds = useMemo(() => {
    const ids = extractComponentIds(draftTopics);
    const previous = componentIdsRef.current;
    const isSameLength = previous.length === ids.length;
    const isSameOrder = isSameLength && previous.every((id, index) => id === ids[index]);

    if (isSameOrder) {
      return previous;
    }

    componentIdsRef.current = ids;
    return ids;
  }, [draftTopics]);

  const draftFlowData = useMemo(
    () => buildDraftFlowData(flowState, componentIds),
    [flowState, componentIds]
  );

  const flowStateTopicIds = useMemo(() => {
    if (!Array.isArray(flowState?.components)) return [];
    return flowState.components.filter(
      (componentId) => componentId !== undefined && componentId !== null
    );
  }, [flowState]);
  const normalizedHydrationComponentIds = useMemo(
    () =>
      componentIds.filter(
        (componentId) =>
          componentId != null && (typeof componentId !== 'string' || componentId.trim().length > 0)
      ),
    [componentIds]
  );
  const hydrationTopicIdsSignature = useMemo(
    () => JSON.stringify([...normalizedHydrationComponentIds].sort()),
    [normalizedHydrationComponentIds]
  );
  const hasEditablePromptRequirements = Array.isArray(flowState?.editablePromptRequirements)
    ? flowState.editablePromptRequirements.length > 0
    : false;

  const draftIdentitySignature = useMemo(
    () =>
      JSON.stringify({
        businessQuestion: flowState?.businessQuestion || '',
        chatSessionId: chatSessionId || null,
        flowId: flowState?.flowId || null,
        narrativeId: flowState?.narrativeId || flowState?.narrative_id || null,
      }),
    [
      chatSessionId,
      flowState?.businessQuestion,
      flowState?.flowId,
      flowState?.narrativeId,
      flowState?.narrative_id,
    ]
  );

  const draftEditablePromptAnswers = useMemo(() => {
    const nextEditablePromptAnswers = { ...(flowState?.editablePromptAnswers || {}) };

    visiblePrompts.forEach((prompt) => {
      if (!Object.prototype.hasOwnProperty.call(selectedAnswers, prompt.promptKey)) return;
      nextEditablePromptAnswers[prompt.promptKey] = selectedAnswers[prompt.promptKey];
    });

    return nextEditablePromptAnswers;
  }, [flowState?.editablePromptAnswers, selectedAnswers, visiblePrompts]);

  const draftNarrativeName = useMemo(() => {
    return promptModalName?.trim() || DEFAULT_DRAFT_NARRATIVE_NAME;
  }, [promptModalName]);
  const topicOrderFlowId = flowId ?? null;
  const flowStateNarrativeName = useMemo(
    () => flowState?.narrativeName?.trim() || DEFAULT_DRAFT_NARRATIVE_NAME,
    [flowState?.narrativeName]
  );
  const isDraftTitleSyncedToFlowState = draftNarrativeName === flowStateNarrativeName;

  useEffect(() => {
    if (hasUserEditedDraftNameRef.current) return;
    if (isPromptModalOpen) return;

    const nextNarrativeName = flowStateNarrativeName;

    setPromptModalName((currentName) => {
      const currentNarrativeName = currentName?.trim() || DEFAULT_DRAFT_NARRATIVE_NAME;
      return currentNarrativeName === nextNarrativeName ? currentName : nextNarrativeName;
    });
  }, [flowStateNarrativeName, isPromptModalOpen]);

  useEffect(() => {
    hasUserEditedDraftNameRef.current = false;
    lastPersistedDraftSignatureRef.current = null;
    draftPersistenceRequestIdRef.current += 1;
    shouldPersistPromptChangesRef.current = false;
    promptSelectionsSnapshotRef.current = null;
    setPromptModalName(flowStateNarrativeName);
  }, [draftIdentitySignature]);

  useEffect(() => {
    latestRunSyncContextRef.current = {
      chatSessionId,
      flowId,
    };
  }, [chatSessionId, flowId]);

  latestPromptRequirementsHydrationContextRef.current = {
    chatSessionId,
    isViewingDraft,
    topicIdsSignature: hydrationTopicIdsSignature,
  };
  latestFlowStateRef.current = flowState;

  useEffect(() => {
    if (!chatSessionId) return;
    if (!isViewingDraft || !onFlowStateChange) return;
    if (!hasDraftTopicCards || !normalizedHydrationComponentIds.length) return;
    if (hasEditablePromptRequirements) return;

    const requestSignature = hydrationTopicIdsSignature;

    if (promptRequirementsHydrationSignatureRef.current === requestSignature) return;
    promptRequirementsHydrationSignatureRef.current = requestSignature;

    const requestId = ++promptRequirementsHydrationRequestIdRef.current;
    const requestContext = {
      chatSessionId,
      isViewingDraft,
      topicIdsSignature: hydrationTopicIdsSignature,
    };
    const hydratePromptRequirements = async () => {
      try {
        const fallbackFlowState = await updateFlowStateTopicIds({
          chatSessionId,
          ...(topicOrderFlowId != null ? { flowId: topicOrderFlowId } : {}),
          topicIds: normalizedHydrationComponentIds,
        });

        if (requestId !== promptRequirementsHydrationRequestIdRef.current) return;
        const latestContext = latestPromptRequirementsHydrationContextRef.current;
        const isStaleRequest =
          latestContext.chatSessionId !== requestContext.chatSessionId ||
          latestContext.isViewingDraft !== requestContext.isViewingDraft ||
          latestContext.topicIdsSignature !== requestContext.topicIdsSignature;
        if (isStaleRequest) return;

        const resolvedPromptRequirements = Array.isArray(
          fallbackFlowState?.editablePromptRequirements
        )
          ? fallbackFlowState.editablePromptRequirements
          : [];
        const hasDefaultPromptAnswers =
          fallbackFlowState?.defaultPromptAnswers &&
          typeof fallbackFlowState.defaultPromptAnswers === 'object';
        if (!resolvedPromptRequirements.length && !hasDefaultPromptAnswers) return;

        const currentFlowState =
          latestFlowStateRef.current && typeof latestFlowStateRef.current === 'object'
            ? latestFlowStateRef.current
            : {};
        const nextFlowState = {
          ...currentFlowState,
          components:
            Array.isArray(currentFlowState?.components) && currentFlowState.components.length > 0
              ? currentFlowState.components
              : normalizedHydrationComponentIds,
          ...(resolvedPromptRequirements.length
            ? {
                editablePromptRequirements: resolvedPromptRequirements,
              }
            : {}),
          ...(hasDefaultPromptAnswers
            ? {
                defaultPromptAnswers: {
                  ...(fallbackFlowState?.defaultPromptAnswers || {}),
                  ...(currentFlowState?.defaultPromptAnswers || {}),
                },
              }
            : {}),
        };
        onFlowStateChange(nextFlowState);
      } catch (error) {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
        if (promptRequirementsHydrationSignatureRef.current === requestSignature) {
          promptRequirementsHydrationSignatureRef.current = null;
        }
        console.error('Failed to hydrate draft prompt requirements', error);
      }
    };

    hydratePromptRequirements();

    return () => {
      if (promptRequirementsHydrationRequestIdRef.current === requestId) {
        promptRequirementsHydrationRequestIdRef.current += 1;
        if (promptRequirementsHydrationSignatureRef.current === requestSignature) {
          promptRequirementsHydrationSignatureRef.current = null;
        }
      }
    };
  }, [
    chatSessionId,
    hasDraftTopicCards,
    hasEditablePromptRequirements,
    hydrationTopicIdsSignature,
    isViewingDraft,
    normalizedHydrationComponentIds,
    onFlowStateChange,
  ]);

  const draftCanvasFlowState = useMemo(() => {
    if (!isViewingDraft || !flowState) return null;

    return {
      ...flowState,
      editablePromptAnswers: draftEditablePromptAnswers,
      narrativeName: draftNarrativeName,
    };
  }, [draftEditablePromptAnswers, draftNarrativeName, flowState, isViewingDraft]);

  const draftCanvasFlowStateSignature = useMemo(() => {
    if (!draftCanvasFlowState) return null;

    return JSON.stringify({
      editablePromptAnswers: draftCanvasFlowState.editablePromptAnswers || {},
      narrativeName: draftCanvasFlowState.narrativeName || '',
    });
  }, [draftCanvasFlowState]);

  useEffect(() => {
    latestDraftPersistContextRef.current = {
      chatSessionId,
      draftIdentitySignature,
      draftSignature: draftCanvasFlowStateSignature,
      flowId,
      isViewingDraft,
    };
  }, [
    chatSessionId,
    draftCanvasFlowStateSignature,
    draftIdentitySignature,
    flowId,
    isViewingDraft,
  ]);

  const currentDraftFlowStateSignature = useMemo(() => {
    if (!flowState) return null;

    return JSON.stringify({
      editablePromptAnswers: flowState?.editablePromptAnswers || {},
      narrativeName: flowState?.narrativeName || '',
    });
  }, [flowState]);

  const enrichedDraftTopics = useMemo(
    () => buildEnrichedDraftTopics(draftTopics, draftDatasetMap),
    [draftDatasetMap, draftTopics]
  );

  const canPersistDraftOrder = Boolean(chatSessionId);
  const canManageManualDrafts = typeof onSelectedTopicsChange === 'function';
  const isDraftActionPending = Boolean(pendingDraftAction);

  const isBuildDisabled = !areRequiredPromptsFilled(visiblePrompts, transformedAnswersMap);
  const hasAllRequiredPrompts = !isBuildDisabled;
  const hasPromptSelections = useMemo(() => {
    return Object.values(transformedAnswersMap || {}).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object') return Object.keys(value).length > 0;
      return Boolean(value);
    });
  }, [transformedAnswersMap]);
  const needsPromptSelection = isViewingDraft && !hasAllRequiredPrompts;
  const isNarrativeProcessing =
    Boolean(flowId) &&
    Boolean(narrativeFlow?.status) &&
    !COMPLETED_OR_ERROR_FLOW_STATUSES.includes(narrativeFlow.status);

  const isToolbarActionLoading = Boolean(isNarrativeProcessing || isBuilding);
  let toolbarActionLabel = 'Select Prompts';
  if (isViewingRun) {
    toolbarActionLabel = 'Reprompt';
  } else if (isViewingDraft && hasAllRequiredPrompts && hasPromptSelections) {
    toolbarActionLabel = 'Create Narrative';
  }
  if (isToolbarActionLoading) {
    toolbarActionLabel = 'Loading';
  }

  const promptsUnavailable = !canSelectPrompts && needsPromptSelection;
  const isToolbarActionDisabled = promptsUnavailable || isToolbarActionLoading;
  const shouldShowToolbar = isViewingRun || (isViewingDraft && hasDraftTopicCards);
  const templateTopicIds = useMemo(() => {
    if (isViewingRun) {
      const runTopicIds = normalizeTopicIds(narrativeFlow?.topic_ids);
      if (runTopicIds.length > 0) {
        return runTopicIds;
      }
      const flowStateTopicIdsFromState = normalizeTopicIds(flowState?.topic_ids);
      if (flowStateTopicIdsFromState.length > 0) {
        return flowStateTopicIdsFromState;
      }
      const normalizedFlowStateTopicIds = normalizeTopicIds(flowStateTopicIds);
      if (normalizedFlowStateTopicIds.length > 0) {
        return normalizedFlowStateTopicIds;
      }
      if (Array.isArray(narrativeFlow?.topics) && narrativeFlow.topics.length > 0) {
        return normalizeTopicIds(
          narrativeFlow.topics.filter((topic) => !topic?.isHidden).map((topic) => topic?.topicId)
        );
      }
      return [];
    }

    const normalizedFlowStateTopicIds = normalizeTopicIds(flowStateTopicIds);
    if (normalizedFlowStateTopicIds.length > 0) return normalizedFlowStateTopicIds;
    const normalizedComponentIds = normalizeTopicIds(componentIds);
    if (normalizedComponentIds.length > 0) return normalizedComponentIds;

    return [];
  }, [
    componentIds,
    flowState?.topic_ids,
    flowStateTopicIds,
    isViewingRun,
    narrativeFlow?.topic_ids,
    narrativeFlow?.topics,
  ]);

  useEffect(() => {
    if (!chatSessionId) return;
    readProject({ projectId: chatSessionId })
      .then((response) => {
        if (response) {
          setProject(response);
          previousProjectNameRef.current = response.name || 'Untitled Project';
        }
      })
      .catch((error) => {
        console.error('Failed to fetch project name', error);
      });
  }, [chatSessionId]);

  const persistTopicOrder = useCallback(
    async (nextIds) => {
      if (!chatSessionId) return null;

      try {
        const updatedFlowState =
          (await updateFlowStateTopicIds({
            chatSessionId,
            ...(topicOrderFlowId != null ? { flowId: topicOrderFlowId } : {}),
            topicIds: nextIds,
          })) ||
          (flowState
            ? { ...flowState, components: nextIds }
            : {
                components: nextIds,
                ...(topicOrderFlowId != null ? { flowId: topicOrderFlowId } : {}),
              });

        if (onFlowStateChange) {
          onFlowStateChange(updatedFlowState);
        }

        return updatedFlowState;
      } catch (error) {
        console.error('Failed to update topic order', error);
        toast.error('We could not update your topic order. Please try again.');
        throw error;
      }
    },
    [chatSessionId, flowState, onFlowStateChange, topicOrderFlowId]
  );

  const syncRunFlowState = useCallback(
    async (topics = []) => {
      if (!chatSessionId) return null;

      const requestId = ++runStateSyncRequestIdRef.current;
      const requestContext = {
        chatSessionId,
        flowId,
      };
      const components = getRunComponentIds(topics);

      const updatedFlowState = await updateFlowState({
        chatSessionId,
        flowState: {
          components,
          flowId,
        },
      });

      const latestContext = latestRunSyncContextRef.current;

      if (requestId !== runStateSyncRequestIdRef.current) return null;
      if (!isRunContextUnchanged(latestContext, requestContext)) return null;

      if (updatedFlowState && onFlowStateChange) {
        onFlowStateChange(updatedFlowState);
      }

      return updatedFlowState;
    },
    [chatSessionId, flowId, onFlowStateChange]
  );

  useEffect(() => {
    if (!flowState || !visiblePrompts.length || isPromptModalOpen) return;

    const hydratedAnswers = extractEditablePromptAnswers(flowState);

    setSelectedAnswers(hydratedAnswers);

    const initialTransformedAnswers = visiblePrompts.reduce((acc, prompt) => {
      const answers = hydratedAnswers?.[prompt.promptKey];
      const hasAnswers = Array.isArray(answers) ? answers.length > 0 : Boolean(answers);

      if (!hasAnswers) return acc;

      try {
        acc[prompt.promptKey] = transformPromptSelections({ answers, prompt });
      } catch (error) {
        console.error('Failed to transform prompt answers from chat flow state:', error);
      }

      return acc;
    }, {});

    setTransformedAnswersMap(initialTransformedAnswers);

    setActivePrompt((currentPrompt) => {
      if (
        currentPrompt &&
        visiblePrompts.some((prompt) => prompt.promptKey === currentPrompt.promptKey)
      ) {
        return currentPrompt;
      }

      return visiblePrompts[0] || null;
    });
  }, [flowState, isPromptModalOpen, visiblePrompts]);

  useEffect(() => {
    if (!draftCanvasFlowState || !onFlowStateChange) return;
    if (isPromptModalOpen) return;
    if (!hasUserEditedDraftNameRef.current && !isDraftTitleSyncedToFlowState) return;
    if (draftCanvasFlowStateSignature === currentDraftFlowStateSignature) {
      if (lastPersistedDraftSignatureRef.current !== draftCanvasFlowStateSignature) {
        lastPersistedDraftSignatureRef.current = draftCanvasFlowStateSignature;
      }
      return;
    }

    onFlowStateChange(draftCanvasFlowState);
  }, [
    currentDraftFlowStateSignature,
    draftCanvasFlowState,
    draftCanvasFlowStateSignature,
    isDraftTitleSyncedToFlowState,
    isPromptModalOpen,
    onFlowStateChange,
  ]);

  useEffect(() => {
    if (!chatSessionId || !draftCanvasFlowState || !draftCanvasFlowStateSignature) return;
    if (isPromptModalOpen) return;
    if (!hasUserEditedDraftNameRef.current && !isDraftTitleSyncedToFlowState) return;
    if (draftCanvasFlowStateSignature === lastPersistedDraftSignatureRef.current) return;

    const requestId = ++draftPersistenceRequestIdRef.current;
    const requestContext = {
      chatSessionId,
      draftIdentitySignature,
      draftSignature: draftCanvasFlowStateSignature,
      flowId,
      requireMatchingDraftSignature: true,
      wasViewingDraft: isViewingDraft,
    };
    const requestSignature = draftCanvasFlowStateSignature;
    const timeoutId = window.setTimeout(async () => {
      try {
        const updatedFlowState = await updateFlowState({
          chatSessionId,
          flowState: draftCanvasFlowState,
        });

        if (requestId !== draftPersistenceRequestIdRef.current) return;
        const latestContext = latestDraftPersistContextRef.current;
        // Guard against out-of-order responses after session/mode/draft changes.
        if (!isDraftContextUnchanged(latestContext, requestContext)) return;

        lastPersistedDraftSignatureRef.current = requestSignature;

        if (updatedFlowState && onFlowStateChange) {
          onFlowStateChange(updatedFlowState);
        }
      } catch (error) {
        console.error('Failed to persist draft flow state', error);
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
      if (draftPersistenceRequestIdRef.current === requestId) {
        draftPersistenceRequestIdRef.current += 1;
      }
    };
  }, [
    chatSessionId,
    draftCanvasFlowState,
    draftCanvasFlowStateSignature,
    draftIdentitySignature,
    flowId,
    isDraftTitleSyncedToFlowState,
    isViewingDraft,
    isPromptModalOpen,
    onFlowStateChange,
  ]);

  useEffect(() => {
    setDraftDatasetMap((prev) => pruneDraftDatasetMap(prev, componentIds));
  }, [componentIds]);

  useEffect(() => {
    const previousOrder = previousManualOrderRef.current || [];
    const orderChanged =
      previousOrder.length !== manualTopicOrder.length ||
      previousOrder.some((id, index) => id !== manualTopicOrder[index]);

    if (!orderChanged) return;

    if (!canPersistDraftOrder || !canManageManualDrafts) {
      manualOrderChangeSourceRef.current = false;
      return;
    }

    previousManualOrderRef.current = manualTopicOrder;

    if (manualOrderChangeSourceRef.current) {
      manualOrderChangeSourceRef.current = false;
      return;
    }

    const combinedOrder = mergeUniqueValues(flowStateTopicIds, manualTopicOrder);
    if (!combinedOrder.length) {
      manualOrderChangeSourceRef.current = false;
      return;
    }
    persistTopicOrder(combinedOrder).catch(() => {});
  }, [
    canManageManualDrafts,
    canPersistDraftOrder,
    flowStateTopicIds,
    manualTopicOrder,
    persistTopicOrder,
  ]);

  useEffect(() => {
    const previousCount = previousManualCountRef.current;
    const currentCount = manualTopicOrder.length;
    let rafId = null;
    let rafId2 = null;

    if (currentCount > previousCount) {
      const addedManualId = manualTopicOrder[currentCount - 1];

      rafId = window.requestAnimationFrame(() => {
        rafId2 = window.requestAnimationFrame(() => {
          const manualCardNode = manualCardRefs.current.get(addedManualId);
          const scrollContainerNode = draftScrollContainerRef.current;

          if (!manualCardNode) return;

          if (scrollContainerNode) {
            const manualCardRect = manualCardNode.getBoundingClientRect();
            const scrollContainerRect = scrollContainerNode.getBoundingClientRect();
            const cardIsTallerThanCanvas = manualCardRect.height >= scrollContainerRect.height;

            if (cardIsTallerThanCanvas) {
              const computedStyles = window.getComputedStyle(manualCardNode);
              const marginTop = Number.parseFloat(computedStyles.marginTop) || 0;
              const manualCardTopRelativeToViewport = manualCardRect.top;
              const scrollContainerTopRelativeToViewport = scrollContainerRect.top;
              const nextScrollTop =
                manualCardTopRelativeToViewport -
                scrollContainerTopRelativeToViewport +
                scrollContainerNode.scrollTop -
                marginTop;

              scrollContainerNode.scrollTo({
                behavior: 'smooth',
                left: 0,
                top: nextScrollTop,
              });
              return;
            }
          }

          manualCardNode.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        });
      });
    }

    previousManualCountRef.current = currentCount;

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (rafId2) window.cancelAnimationFrame(rafId2);
    };
  }, [manualTopicOrder]);

  useEffect(() => {
    const idsToFetch = componentIds.filter((id) => !draftDatasetMap[id]);
    if (!idsToFetch.length) return;

    const controller = new AbortController();

    const fetchDraftTopics = async () => {
      try {
        const data = await readDraftTopics({
          componentIds: idsToFetch,
          signal: controller.signal,
        });

        setDraftDatasetMap((prev) => {
          const resolvedData = idsToFetch.reduce((acc, componentId) => {
            acc[componentId] = data?.[componentId] || { dataset: null };
            return acc;
          }, {});

          return { ...prev, ...resolvedData };
        });
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
        console.error('Error fetching draft topics:', error);
      }
    };

    fetchDraftTopics();

    return () => {
      controller.abort();
    };
  }, [componentIds, draftDatasetMap]);

  const handlePromptModalVisibilityChange = (nextIsOpen) => {
    if (!nextIsOpen) {
      if (!shouldPersistPromptChangesRef.current && promptSelectionsSnapshotRef.current) {
        const {
          draftIdentitySignature: snapshotDraftIdentitySignature,
          flowStateNarrativeName: snapshotFlowStateNarrativeName,
          name,
          selectedAnswers: answersSnapshot,
          transformedAnswersMap: transformedAnswersSnapshot,
        } = promptSelectionsSnapshotRef.current;

        if (snapshotDraftIdentitySignature === draftIdentitySignature) {
          if (snapshotFlowStateNarrativeName === flowStateNarrativeName) {
            setPromptModalName(name);
          }
          setSelectedAnswers(answersSnapshot);
          setTransformedAnswersMap(transformedAnswersSnapshot);
        }
      }

      shouldPersistPromptChangesRef.current = false;
      promptSelectionsSnapshotRef.current = null;
    }

    setIsPromptModalOpen(nextIsOpen);
  };

  const handleSelectPrompts = (prompt = null) => {
    if (!canSelectPrompts) return;

    setBuildError(null);
    promptSelectionsSnapshotRef.current = {
      draftIdentitySignature,
      flowStateNarrativeName,
      name: promptModalName,
      selectedAnswers,
      transformedAnswersMap,
    };
    shouldPersistPromptChangesRef.current = false;
    if (prompt) {
      const promptKey = prompt?.promptKey || prompt?.id || null;
      const resolvedPrompt =
        visiblePrompts.find(
          (visiblePrompt) => visiblePrompt.promptKey === promptKey || visiblePrompt.id === promptKey
        ) || prompt;
      setActivePrompt(resolvedPrompt);
    }
    setIsPromptModalOpen(true);
  };

  const handleRunReprompt = useCallback((prompt = null) => {
    setSelectedRunPrompt(prompt);
    setIsPromptModalOpen(true);
  }, []);

  const handleRunModalVisibilityChange = useCallback((nextIsOpen) => {
    if (!nextIsOpen) {
      setSelectedRunPrompt(null);
    }
    setIsPromptModalOpen(nextIsOpen);
  }, []);

  const handleBuildFromPrompts = async () => {
    if (isBuildDisabled || isBuilding) return;

    const promptAnswers = buildPromptAnswersPayload(
      transformedAnswersMap,
      Object.entries({
        ...(flowState?.defaultPromptAnswers || {}),
        ...(flowState?.readOnlyPromptAnswers || {}),
      }).reduce((acc, [field, value]) => {
        const isVisiblePrompt = visiblePrompts.some((prompt) => prompt.promptKey === field);
        if (!isVisiblePrompt) {
          acc[field] = value;
        }
        return acc;
      }, {})
    );

    if (!Object.keys(promptAnswers).length) {
      const errorMessage = 'Please complete the required prompts before building your Narrative.';
      setBuildError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    setBuildError(null);
    // Invalidate any in-flight draft save so delayed responses cannot overwrite run state.
    draftPersistenceRequestIdRef.current += 1;
    setIsBuilding(true);

    try {
      const createdNarrative = await createNarrative({
        flowId,
        id: 'dynamic',
        promptAnswers,
        title: draftNarrativeName,
        topicIds: componentIds,
      });

      if (!createdNarrative?.flowId) {
        throw new Error('Narrative build did not return a flow identifier.');
      }

      if (chatSessionId) {
        try {
          const requestContext = {
            chatSessionId,
            draftIdentitySignature,
            flowId,
            requireMatchingDraftSignature: false,
            wasViewingDraft: isViewingDraft,
          };
          const updatedFlowState = await updateFlowState({
            chatSessionId,
            flowState: {
              ...(draftCanvasFlowState || flowState || {}),
              components: componentIds,
              editablePromptAnswers: draftEditablePromptAnswers,
              flowId: createdNarrative.flowId,
              narrativeName: draftNarrativeName,
            },
          });

          const latestContext = latestDraftPersistContextRef.current;
          // Guard against late build-sync responses after session/view/draft changes.
          const canApplyBuildSync = isDraftContextUnchanged(latestContext, requestContext);
          if (canApplyBuildSync && updatedFlowState && onFlowStateChange) {
            onFlowStateChange(updatedFlowState);
          }
        } catch (syncError) {
          console.error('Failed to sync active run after build', syncError);
        }
      }

      if (onFlowSelected) {
        onFlowSelected(createdNarrative.flowId);
      }

      shouldPersistPromptChangesRef.current = true;
      handlePromptModalVisibilityChange(false);
    } catch (error) {
      const promptErrors = error?.response?.data?.['Prompt Errors'] ?? [];
      const errorMessage =
        promptErrors[0] || 'There was an error building your Narrative. Please try again.';

      setBuildError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to build Narrative:', error);
    } finally {
      setIsBuilding(false);
    }
  };

  const registerManualCardRef = useCallback((componentId) => {
    if (componentId == null) return null;

    if (!manualCardRefCallbacks.current.has(componentId)) {
      manualCardRefCallbacks.current.set(componentId, (node) => {
        if (node) {
          manualCardRefs.current.set(componentId, node);
        } else {
          manualCardRefs.current.delete(componentId);
          manualCardRefCallbacks.current.delete(componentId);
        }
      });
    }

    return manualCardRefCallbacks.current.get(componentId);
  }, []);

  const handleToolbarAction = () => {
    if (isBuilding || isToolbarActionLoading) return;

    if (isViewingRun) {
      handleRunReprompt();
      return;
    }

    if (isViewingDraft && hasAllRequiredPrompts) {
      handleBuildFromPrompts();
      return;
    }

    handleSelectPrompts();
  };

  const handleDraftTopicAction = useCallback(
    async (componentId, action) => {
      if (!canPersistDraftOrder || !componentId) return;

      const currentIds = flowStateTopicIds;
      const currentIndex = currentIds.indexOf(componentId);
      if (currentIndex === -1) return;

      const nextIds = [...currentIds];

      if (action === 'up' && currentIndex > 0) {
        [nextIds[currentIndex - 1], nextIds[currentIndex]] = [
          nextIds[currentIndex],
          nextIds[currentIndex - 1],
        ];
      } else if (action === 'down' && currentIndex < nextIds.length - 1) {
        [nextIds[currentIndex + 1], nextIds[currentIndex]] = [
          nextIds[currentIndex],
          nextIds[currentIndex + 1],
        ];
      } else if (action === 'remove') {
        nextIds.splice(currentIndex, 1);
      } else {
        return;
      }

      const noChange =
        nextIds.length === currentIds.length &&
        nextIds.every((id, index) => id === currentIds[index]);
      if (noChange) return;

      setPendingDraftAction({ action, componentId });
      try {
        await persistTopicOrder(nextIds);
        if (action === 'remove' && typeof onSelectedTopicsChange === 'function') {
          manualOrderChangeSourceRef.current = true;
          onSelectedTopicsChange((previousTopics) => {
            if (!Array.isArray(previousTopics)) return previousTopics;
            return previousTopics.filter((topic) => topic?.id !== componentId);
          });
        }
      } catch (error) {
        console.error('Failed to update draft topic order', error);
        // toast already shown inside persistTopicOrder
      } finally {
        setPendingDraftAction(null);
      }
    },
    [canPersistDraftOrder, flowStateTopicIds, onSelectedTopicsChange, persistTopicOrder]
  );

  const handleManualTopicAction = useCallback(
    (componentId, action) => {
      if (typeof onSelectedTopicsChange !== 'function' || !componentId) return;

      let nextManualTopics = null;
      manualOrderChangeSourceRef.current = true;

      onSelectedTopicsChange((prevTopics) => {
        if (!Array.isArray(prevTopics)) return prevTopics;
        const index = prevTopics.findIndex((topic) => topic?.id === componentId);
        if (index === -1) return prevTopics;

        const nextTopics = [...prevTopics];

        if (action === 'remove') {
          nextTopics.splice(index, 1);
        } else if (action === 'up' && index > 0) {
          [nextTopics[index - 1], nextTopics[index]] = [nextTopics[index], nextTopics[index - 1]];
        } else if (action === 'down' && index < nextTopics.length - 1) {
          [nextTopics[index + 1], nextTopics[index]] = [nextTopics[index], nextTopics[index + 1]];
        } else {
          manualOrderChangeSourceRef.current = false;
          return prevTopics;
        }

        nextManualTopics = nextTopics;
        return nextTopics;
      });

      if (nextManualTopics) {
        const manualIds = nextManualTopics.map((topic) => topic.id);
        const combinedOrder = mergeUniqueValues(flowStateTopicIds, manualIds);
        if (!combinedOrder.length) {
          manualOrderChangeSourceRef.current = false;
          return;
        }
        persistTopicOrder(combinedOrder)
          .catch(() => {})
          .finally(() => {
            manualOrderChangeSourceRef.current = false;
          });
      } else {
        manualOrderChangeSourceRef.current = false;
      }
    },
    [flowStateTopicIds, onSelectedTopicsChange, persistTopicOrder]
  );

  const handleRunTopicAction = useCallback(
    async ({ action, flowTopics = [], setFlow, topic }) => {
      if (
        !isViewingRun ||
        !topic?.id ||
        typeof setFlow !== 'function' ||
        isNarrativeProcessing ||
        Boolean(pendingRunTopicAction)
      )
        return;

      const flowTopicId = topic.id;
      const componentId = getRunTopicComponentId(topic);
      const topicsList = Array.isArray(flowTopics) ? flowTopics : [];
      const originalTopics = topicsList.slice();
      const applyTopics = (nextTopics) => {
        setFlow((prevFlow) => {
          if (!prevFlow) return prevFlow;
          return { ...prevFlow, topics: nextTopics };
        });
      };

      setPendingRunTopicAction({ action, topicId: flowTopicId });

      try {
        if ((action === 'up' || action === 'down') && flowId) {
          const currentIndex = topicsList.findIndex((item) => item?.id === flowTopicId);
          if (currentIndex === -1) return;
          const destinationIndex = action === 'up' ? currentIndex - 1 : currentIndex + 1;
          if (destinationIndex < 0 || destinationIndex >= topicsList.length) return;

          const nextTopics = topicsList.slice();
          [nextTopics[currentIndex], nextTopics[destinationIndex]] = [
            nextTopics[destinationIndex],
            nextTopics[currentIndex],
          ];
          applyTopics(nextTopics);
          await reorderFlowTopics({
            flowId,
            topicIds: nextTopics.map((item) => item.id),
          });
          try {
            await syncRunFlowState(nextTopics);
          } catch (syncError) {
            console.error('Failed to sync run state after reorder', syncError);
            toast.error('We could not sync the chat state. Please refresh and try again.');
          }
        } else if (action === 'exclude' || action === 'include') {
          const shouldHide = action === 'exclude';
          const alreadyMatches = topic.isHidden === shouldHide;
          if (alreadyMatches) return;

          const nextTopics = topicsList.map((item) =>
            item.id === flowTopicId ? { ...item, isHidden: shouldHide } : item
          );
          applyTopics(nextTopics);
          await updateTopicVisibility({ id: flowTopicId, isHidden: shouldHide });
          try {
            await syncRunFlowState(nextTopics);
          } catch (syncError) {
            console.error('Failed to sync run state after visibility change', syncError);
            toast.error('We could not sync the chat state. Please refresh and try again.');
          }
        } else if (action === 'delete') {
          const nextTopics = topicsList.filter((item) => item.id !== flowTopicId);
          applyTopics(nextTopics);
          await softDeleteFlowTopic({ topicId: flowTopicId });

          if (typeof onSelectedTopicsChange === 'function' && componentId != null) {
            onSelectedTopicsChange((previousTopics) => {
              if (!Array.isArray(previousTopics)) return previousTopics;
              const nextSelectedTopics = previousTopics.filter(
                (topicItem) => String(topicItem?.id) !== String(componentId)
              );
              return nextSelectedTopics;
            });
          }

          const currentComponentIds = Array.isArray(flowState?.components)
            ? flowState.components
            : [];
          const nextComponentIdsFromRun = topicsList
            .filter((item) => String(item?.id) !== String(flowTopicId))
            .map((item) => getRunTopicComponentId(item))
            .filter((id) => id != null);
          const nextComponentIds =
            nextComponentIdsFromRun.length > 0
              ? nextComponentIdsFromRun
              : currentComponentIds.filter((id) => String(id) !== String(componentId));
          const optimisticFlowState = flowState
            ? { ...flowState, components: nextComponentIds }
            : { components: nextComponentIds, flowId };
          if (onFlowStateChange) {
            onFlowStateChange(optimisticFlowState);
          }

          if (chatSessionId && componentId != null) {
            if (nextComponentIds.length !== currentComponentIds.length) {
              try {
                const updatedFlowState =
                  (await updateFlowStateTopicIds({
                    chatSessionId,
                    flowId,
                    topicIds: nextComponentIds,
                  })) || optimisticFlowState;

                if (onFlowStateChange) {
                  onFlowStateChange(updatedFlowState);
                }
              } catch (syncError) {
                console.error('Failed to sync flow state after topic deletion', syncError);
                if (onFlowStateChange) {
                  onFlowStateChange(optimisticFlowState);
                }
                toast.error('We could not sync the chat state. Please refresh and try again.');
              }
            } else {
              try {
                await syncRunFlowState(nextTopics);
              } catch (syncError) {
                console.error('Failed to sync run state after topic deletion', syncError);
                toast.error('We could not sync the chat state. Please refresh and try again.');
              }
            }
          }
        } else {
          return;
        }
      } catch (error) {
        console.error('Failed to process run topic action', error);
        toast.error('We could not complete that action. Please try again.');
        applyTopics(originalTopics);
      } finally {
        setPendingRunTopicAction(null);
        if (action === 'delete') {
          setTopicPendingDeletion(null);
        }
      }
    },
    [
      chatSessionId,
      flowId,
      flowState,
      isNarrativeProcessing,
      isViewingRun,
      onFlowStateChange,
      pendingRunTopicAction,
      setTopicPendingDeletion,
      syncRunFlowState,
    ]
  );

  const runTopicControlsBuilder = useCallback(
    ({ index, setFlow, topic, topics }) => {
      if (!isViewingRun || !topic?.id || !Array.isArray(topics)) return null;

      const totalTopics = topics.length;
      const isExcluded = Boolean(topic?.isHidden);
      const isPending = Boolean(pendingRunTopicAction);
      const shouldDisableRunControls = isPending || isNarrativeProcessing;

      const controls = {
        primaryAction: {
          disabled: shouldDisableRunControls,
          label: isExcluded ? 'Include' : 'Exclude',
          onClick: () =>
            handleRunTopicAction({
              action: isExcluded ? 'include' : 'exclude',
              flowTopics: topics,
              setFlow,
              topic,
            }),
        },
        secondaryActions: canManageRunTopics
          ? [
              {
                disabled: shouldDisableRunControls || totalTopics < 2 || index <= 0,
                label: 'Move Up',
                onClick: () =>
                  handleRunTopicAction({
                    action: 'up',
                    flowTopics: topics,
                    setFlow,
                    topic,
                  }),
              },
              {
                disabled: shouldDisableRunControls || totalTopics < 2 || index === totalTopics - 1,
                label: 'Move Down',
                onClick: () =>
                  handleRunTopicAction({
                    action: 'down',
                    flowTopics: topics,
                    setFlow,
                    topic,
                  }),
              },
            ]
          : [],
      };

      if (isExcluded && canManageRunTopics) {
        controls.secondaryActions.unshift({
          disabled: shouldDisableRunControls,
          label: 'Remove',
          onClick: () =>
            setTopicPendingDeletion({
              flowTopics: topics,
              setFlow,
              topic,
            }),
        });
      }

      return controls;
    },
    [
      canManageRunTopics,
      handleRunTopicAction,
      isNarrativeProcessing,
      isViewingRun,
      pendingRunTopicAction,
      setTopicPendingDeletion,
    ]
  );

  const preBuildModal =
    !isViewingRun && canSelectPrompts ? (
      <PreBuildModal
        activePrompt={activePrompt}
        buildError={buildError}
        getPillTooltip={getPromptPillTooltip}
        getPillValue={getPromptPillValue}
        handleBuild={handleBuildFromPrompts}
        headerIcon={null}
        isBuildDisabled={isBuildDisabled}
        isBuilding={isBuilding}
        isLoading={false}
        isModalOpen={isPromptModalOpen}
        name={promptModalName}
        prompts={visiblePrompts}
        routeLabel={DEFAULT_PROMPT_ROUTE_LABEL}
        sameLevelPrompts={flowState?.sameLevelPrompts || []}
        searchPrompts={flowState?.searchPrompts || []}
        selectedAnswers={selectedAnswers}
        setActivePrompt={setActivePrompt}
        setIsModalOpen={handlePromptModalVisibilityChange}
        setName={setPromptModalName}
        setSelectedAnswers={setSelectedAnswers}
        setTransformedAnswersMap={setTransformedAnswersMap}
        transformedAnswersMap={transformedAnswersMap}
        transformPromptSelections={transformPromptSelections}
        type="Narrative"
      />
    ) : null;

  const deleteConfirmationModal = topicPendingDeletion ? (
    <ConfirmRemoveTopicModal
      isActive={Boolean(topicPendingDeletion)}
      isConfirmDisabled={
        pendingRunTopicAction?.action === 'delete' &&
        pendingRunTopicAction?.topicId === topicPendingDeletion?.topic?.id
      }
      onCancel={() => setTopicPendingDeletion(null)}
      onConfirm={() =>
        handleRunTopicAction({
          ...topicPendingDeletion,
          action: 'delete',
        })
      }
    />
  ) : null;

  const handleFavoriteProject = (isFavorite) => {
    favoriteProject({ chatSessionId, isFavorite })
      .then(() => {
        setProject((prevProject) => {
          return { ...prevProject, isFavorite };
        });
      })
      .catch((error) => {
        console.error('Failed to update favorite project status', error);
        toast.error('We could not update your favorite project. Please try again.');
      });
  };

  const renderProjectTitle = () => {
    return (
      <div className={styles['project-name-banner']}>
        <FavoriteToggle
          isFavorite={project.isFavorite}
          onChange={() => handleFavoriteProject(!project.isFavorite)}
        />
        <EditableTitle
          className={styles['project-name-editable']}
          onChange={handleProjectNameChange}
          value={project?.name || ''}
        />
      </div>
    );
  };

  const handleProjectNameChange = (nextName) => {
    setProject((prevProject) => ({ ...prevProject, name: nextName }));
  };

  const handleRenameProject = () => {
    renameProject({ name: project.name, projectId: chatSessionId })
      .then(() => {
        previousProjectNameRef.current = project.name;
      })
      .catch((error) => {
        console.error('Failed to rename project', error);
        toast.error('Unable to rename project.');
      });
  };

  useEffect(() => {
    const currentName = project?.name.trim() || '';
    const previousName = previousProjectNameRef.current?.trim() || '';
    if (!currentName || currentName === previousName) return;
    const timer = setTimeout(() => {
      handleRenameProject();
    }, 2000);
    return () => clearTimeout(timer);
  }, [project?.name]);

  const resolveRunComponentIds = useCallback(
    (flow) => {
      if (Array.isArray(flow?.topic_ids)) return flow.topic_ids;
      if (Array.isArray(flow?.topics)) return getRunComponentIds(flow.topics);
      if (Array.isArray(flowState?.components)) return flowState.components;
      return null;
    },
    [flowState?.components]
  );

  if (isViewingRun) {
    return (
      <div className={styles['narratives-canvas-container']} ref={draftScrollContainerRef}>
        <div className={styles['narratives-canvas-content']}>
          <NarrativeFlowView
            flowId={flowId}
            getTopicControls={runTopicControlsBuilder}
            hidePageTitle
            key={`${flowId}-${runFlowRefreshKey}`}
            onReprompt={handleRunReprompt}
            renderAfterContent={({ flow, narrativeRoute }) => (
              <>
                <RunFlowTopicsObserver
                  flow={flow}
                  onRunFlowTopicIdsChange={onRunFlowTopicIdsChange}
                />
                {isPromptModalOpen ? (
                  <ModalBuildNarrative
                    flowId={flow?.id}
                    onNarrativeBuilt={(nextFlowId) => {
                      const runComponentIds = resolveRunComponentIds(flow);
                      const syncRequestId = ++runStateSyncRequestIdRef.current;
                      const requestContext = {
                        allowNextFlowIdMatch: true,
                        chatSessionId,
                        flowId,
                        nextFlowId,
                      };

                      if (chatSessionId && Array.isArray(runComponentIds)) {
                        updateFlowState({
                          chatSessionId,
                          flowState: {
                            components: runComponentIds,
                            flowId: nextFlowId,
                          },
                        })
                          .then((updatedFlowState) => {
                            if (syncRequestId !== runStateSyncRequestIdRef.current) return;
                            const latestContext = latestRunSyncContextRef.current;
                            if (!isRunContextUnchanged(latestContext, requestContext)) return;
                            if (updatedFlowState && onFlowStateChange) {
                              onFlowStateChange(updatedFlowState);
                            }
                          })
                          .catch((error) => {
                            console.error('Failed to sync active run after reprompt', error);
                          });
                      }

                      handleRunModalVisibilityChange(false);
                      if (typeof onFlowSelected === 'function') {
                        onFlowSelected(nextFlowId);
                      }
                    }}
                    route={narrativeRoute}
                    selectedPrompt={selectedRunPrompt}
                    setIsModalOpen={handleRunModalVisibilityChange}
                    topicIds={flow?.topic_ids}
                  />
                ) : null}
              </>
            )}
            renderProjectTitle={renderProjectTitle}
            useWindowScroll={false}
          />
        </div>
        <Footer />
        {shouldShowToolbar && (
          <CanvasFloatingToolbar
            canAccessChat={canManageRunTopics}
            onSelectPrompts={handleToolbarAction}
            selectPromptsDisabled={isToolbarActionDisabled}
            selectPromptsLabel={toolbarActionLabel}
            selectPromptsLoading={isToolbarActionLoading}
            topicIds={templateTopicIds}
          />
        )}
        {preBuildModal}
        {deleteConfirmationModal}
      </div>
    );
  }

  return (
    <div className={styles['narratives-canvas-container']} ref={draftScrollContainerRef}>
      <div className={styles['narratives-canvas-content']}>
        {draftTopics.length > 0 && (
          <div className={styles['narratives-canvas-draft-view']}>
            {draftFlowData && (
              <>
                {project && renderProjectTitle()}
                <NarrativeTitle
                  downloadData={EMPTY_DOWNLOAD_MAP}
                  flowData={draftFlowData}
                  handleReprompt={handleSelectPrompts}
                  isDraft
                  selectedPromptsOverride={visiblePrompts}
                  setFlow={(updater) => {
                    const nextFlow =
                      typeof updater === 'function' ? updater({ name: promptModalName }) : updater;
                    const nextName = nextFlow?.name?.trim();
                    const currentName = draftNarrativeName?.trim() || DEFAULT_DRAFT_NARRATIVE_NAME;
                    const hasChangedTitle = nextName
                      ? nextName !== currentName
                      : currentName !== DEFAULT_DRAFT_NARRATIVE_NAME;

                    if (hasChangedTitle) {
                      hasUserEditedDraftNameRef.current = true;
                    }

                    if (nextName) {
                      setPromptModalName(nextName);
                    } else {
                      setPromptModalName(DEFAULT_DRAFT_NARRATIVE_NAME);
                    }
                  }}
                  titleOverride={draftNarrativeName}
                />
              </>
            )}

            <motion.div className={styles['narratives-canvas-draft-grid']} layout>
              <AnimatePresence initial={false} mode="popLayout">
                {enrichedDraftTopics.map((draft, index) => {
                  const flowIndex = flowStateTopicIds.indexOf(draft.componentId);
                  const isFlowStateDraft = flowIndex > -1;
                  const topicInManualOrder = manualTopicOrder.indexOf(draft.componentId);
                  const isManualDraft = !isFlowStateDraft && topicInManualOrder > -1;
                  let topicControls = null;

                  if (isFlowStateDraft && canPersistDraftOrder) {
                    topicControls = {
                      primaryAction: {
                        disabled: isDraftActionPending,
                        label: 'Remove',
                        onClick: () => handleDraftTopicAction(draft.componentId, 'remove'),
                      },
                      secondaryActions: [
                        {
                          disabled:
                            isDraftActionPending || flowIndex <= 0 || flowStateTopicIds.length < 2,
                          label: 'Move Up',
                          onClick: () => handleDraftTopicAction(draft.componentId, 'up'),
                        },
                        {
                          disabled:
                            isDraftActionPending ||
                            flowIndex === -1 ||
                            flowIndex === flowStateTopicIds.length - 1 ||
                            flowStateTopicIds.length < 2,
                          label: 'Move Down',
                          onClick: () => handleDraftTopicAction(draft.componentId, 'down'),
                        },
                      ],
                    };
                  } else if (
                    !isFlowStateDraft &&
                    canManageManualDrafts &&
                    topicInManualOrder > -1
                  ) {
                    const manualCount = manualTopicOrder.length;
                    topicControls = {
                      primaryAction: {
                        disabled: false,
                        label: 'Remove',
                        onClick: () => handleManualTopicAction(draft.componentId, 'remove'),
                      },
                      secondaryActions: [
                        {
                          disabled: manualCount < 2 || topicInManualOrder <= 0,
                          label: 'Move Up',
                          onClick: () => handleManualTopicAction(draft.componentId, 'up'),
                        },
                        {
                          disabled: manualCount < 2 || topicInManualOrder === manualCount - 1,
                          label: 'Move Down',
                          onClick: () => handleManualTopicAction(draft.componentId, 'down'),
                        },
                      ],
                    };
                  }

                  return (
                    <motion.div
                      className={styles['narratives-canvas-draft-card']}
                      data-testid={draft.componentId}
                      key={`draft-topic-${draft.componentId}`}
                      layout
                      layoutId={`draft-topic-${draft.componentId}`}
                      {...TOPIC_CARD_MOTION_PROPS}
                    >
                      <div ref={isManualDraft ? registerManualCardRef(draft.componentId) : null}>
                        <NarrativeTopicCard
                          initialTopicData={buildDraftTopicData(draft)}
                          isDraftCard
                          keyIndex={index}
                          onPreview={() => {}}
                          setDownloadData={() => {}}
                          topic={buildTopicShell(draft)}
                          topicControls={topicControls}
                          useWindowScroll={false}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          </div>
        )}
      </div>

      <Footer />
      {shouldShowToolbar && (
        <CanvasFloatingToolbar
          canAccessChat={canManageRunTopics}
          onSelectPrompts={handleToolbarAction}
          selectPromptsDisabled={isToolbarActionDisabled}
          selectPromptsLabel={toolbarActionLabel}
          selectPromptsLoading={isToolbarActionLoading}
          topicIds={templateTopicIds}
        />
      )}
      {preBuildModal}
      {deleteConfirmationModal}
    </div>
  );
};

NarrativesCanvas.propTypes = {
  chatSessionId: PropTypes.string,
  flowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flowState: PropTypes.shape({
    businessQuestion: PropTypes.string,
    components: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    defaultPromptAnswers: PropTypes.object,
    editablePromptAnswers: PropTypes.object,
    editablePromptRequirements: PropTypes.array,
    flowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    narrative_id: PropTypes.string,
    narrativeId: PropTypes.string,
    narrativeName: PropTypes.string,
    readOnlyPromptAnswers: PropTypes.object,
    sameLevelPrompts: PropTypes.array,
    searchPrompts: PropTypes.array,
    topic_ids: PropTypes.arrayOf(PropTypes.string),
  }),
  onFlowSelected: PropTypes.func,
  onFlowStateChange: PropTypes.func,
  onRunFlowTopicIdsChange: PropTypes.func,
  onSelectedTopicsChange: PropTypes.func,
  runFlowRefreshKey: PropTypes.number,
  selectedTopicCards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string,
      vizType: PropTypes.string,
    })
  ),
};

export default NarrativesCanvas;
